import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { DeceasedClient } from '@frontend/deceased/api';
import { DeceasedState, IDeceased } from '@frontend/deceased/types';
import { ErrorHandler } from '@frontend/error-handler';
import { useLocale } from '@frontend/lang';
import { useEffect, useState } from 'react';

export interface DeceasedSummaryCardProps {
    accountId: string | null | undefined;
    deceasedId: string | null | undefined;
}

interface ViewProps {
    name: string | null;
    statusLabel: React.ReactNode | null;
    dateOfDeathLabel: string | null;
    elysLabel: string | null;
    cremationLabel: string | null;
    isLoading: boolean;
    show: boolean;
}

function formatOptionalDate(value: string | null | undefined, locale: string, format: DateFormat): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return formatDateTimeWithLocale(date, locale, format);
}

const useDeceasedSummaryCard = (props: DeceasedSummaryCardProps): ViewProps => {
    const { locale } = useLocale();
    const [deceased, setDeceased] = useState<IDeceased | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!props.accountId || !props.deceasedId) {
            setDeceased(null);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        DeceasedClient.fetchDeceased(props.accountId, props.deceasedId)
            .then((result) => {
                if (!cancelled) setDeceased(result ?? null);
            })
            .catch((error) => {
                if (!cancelled) {
                    ErrorHandler.handleError({ error });
                    setDeceased(null);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [props.accountId, props.deceasedId]);

    const name = deceased?.name?.trim() || null;
    const statusLabel = deceased ? (DeceasedState.getByValue(deceased.state)?.display ?? DeceasedState.format(deceased.state)) : null;
    const dateOfDeathLabel = formatOptionalDate(deceased?.date_of_death, locale ?? 'en', DateFormat.DD_MMMM_YYYY);
    const elysLabel = deceased?.elys_number?.trim() || null;
    const cremationLabel = formatOptionalDate(deceased?.cremation_timestamp, locale ?? 'en', DateFormat.DD_MMMM_HH_MM);
    const hasContent = !!name || !!statusLabel || !!dateOfDeathLabel || !!elysLabel || !!cremationLabel;

    return {
        name,
        statusLabel,
        dateOfDeathLabel,
        elysLabel,
        cremationLabel,
        isLoading,
        show: !!props.deceasedId && !!props.accountId && (isLoading || hasContent)
    };
};

export default useDeceasedSummaryCard;
